import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Sparkles, Droplet, ShieldCheck, FlaskConical } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { AFFILIATE_URLS, onAffiliateClick } from "../config/affiliate";

const BASE = "https://www.peptidesformedspas.org";
const SHOP_URL = AFFILIATE_URLS.topicals;
const TRACK = (loc) => onAffiliateClick({ partner: "paramount", product: `topicals-${loc}`, url: SHOP_URL });

const HIGHLIGHTS = [
  {
    icon: Droplet,
    title: "Non-injectable formats",
    body: "Topicals deliver active peptides through serums, creams, and emulsions designed for skin-application research — no injection workflow required.",
  },
  {
    icon: Sparkles,
    title: "Aesthetic-research formulations",
    body: "Common topical actives include GHK-Cu, Argireline, Matrixyl, Snap-8, and Copper Tripeptide-1 — all studied in cosmetic and dermatology research.",
  },
  {
    icon: FlaskConical,
    title: "Formulation matters",
    body: "Outcomes for any topical depend heavily on pH, vehicle, encapsulation, packaging, and concentration — cosmetic chemistry is its own discipline.",
  },
  {
    icon: ShieldCheck,
    title: "Research-only catalog",
    body: "All listings are research chemicals supplied for laboratory research purposes only. Not FDA-approved drugs. Not for clinical or human use.",
  },
];

const COMMON_TOPICAL_ACTIVES = [
  { name: "GHK-Cu (Copper Tripeptide-1)", note: "Most-studied copper peptide in dermatology cell-culture and animal research" },
  { name: "Argireline (Acetyl Hexapeptide-8)", note: "Hexapeptide examined in expression-line cosmetic research" },
  { name: "Matrixyl (Palmitoyl Pentapeptide-4)", note: "Pentapeptide signaling-research target in fibroblast studies" },
  { name: "Snap-8 (Acetyl Octapeptide-3)", note: "Octapeptide studied in cosmetic-research literature" },
  { name: "Glutathione (topical formulations)", note: "Tripeptide antioxidant — bioavailability is highly formulation-dependent" },
];

const TopicalsPage = () => {
  usePageSEO();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${BASE}/topical-peptides#webpage`,
      url: `${BASE}/topical-peptides`,
      name: "Topical Peptides | Med Spa Research Catalog",
      description:
        "Topical research peptides for cosmetic and dermatology research: GHK-Cu, Argireline, Matrixyl, Snap-8, and topical glutathione. Affiliate catalog via Paramount Peptides — research-only, not for human or animal use.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      breadcrumb: { "@id": `${BASE}/topical-peptides#breadcrumb` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${BASE}/topical-peptides#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Topical Peptides", item: `${BASE}/topical-peptides` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${BASE}/topical-peptides#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "What are topical peptides?",
          acceptedAnswer: { "@type": "Answer", text: "Topical peptides are short amino-acid chains formulated into serums, creams, gels, or emulsions for skin-application research. Common actives include GHK-Cu, Argireline, Matrixyl, Snap-8, and topical glutathione formulations. They are studied in cosmetic and dermatology research contexts." }
        },
        {
          "@type": "Question",
          name: "Are topical peptides FDA-approved drugs?",
          acceptedAnswer: { "@type": "Answer", text: "Generally no. Most topical peptides are sold as cosmetic-ingredient research materials or research chemicals. They are not FDA-approved drugs and are not labeled for human or animal use in the research-chemical product class. Cosmetic creams sold at retail that contain peptides are a different product class governed by separate cosmetic regulations." }
        },
        {
          "@type": "Question",
          name: "Why does formulation matter so much?",
          acceptedAnswer: { "@type": "Answer", text: "Outcomes for topical peptides depend on pH, vehicle, encapsulation, packaging stability, concentration, and skin-permeation properties. Two products with the same active peptide can perform very differently because of the surrounding cosmetic chemistry." }
        },
        {
          "@type": "Question",
          name: "Does PeptidesForMedSpas earn a referral fee?",
          acceptedAnswer: { "@type": "Answer", text: "Yes. We may receive a referral credit when you purchase from Paramount Peptides through our affiliate link. This is disclosed inline in compliance with FTC endorsement guidelines. The price you pay is set by Paramount Peptides and is not affected by the referral relationship." }
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="topicals-page">
      <Header />
      <JsonLd id="topicals-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e8c5b8] rounded-full mb-5">
            <Droplet className="w-3.5 h-3.5 text-[#b89a6d]" />
            <span className="text-xs uppercase tracking-wider text-[#b89a6d] font-semibold">
              Topical Peptides
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6 leading-tight" data-testid="topicals-h1">
            Topical peptides for
            <span className="block bg-gradient-to-r from-[#d4a5ad] to-[#b89a6d] bg-clip-text text-transparent">
              cosmetic &amp; dermatology research
            </span>
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed mb-8">
            Non-injectable, skin-application peptide formulations studied in cosmetic and dermatology
            research contexts. Browse the topical catalog through our affiliate partner{" "}
            <strong className="text-[#3a2a26]">Paramount Peptides</strong>. All listings are research
            chemicals supplied for laboratory research purposes only.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <a
              href={SHOP_URL}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-testid="topicals-hero-cta"
              onClick={TRACK("hero")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
            >
              Shop Topical Peptides
              <ExternalLink className="w-4 h-4" />
            </a>
            <span className="text-sm text-[#a09186]">
              Catalog hosted by Paramount Peptides · affiliate disclosure below
            </span>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-2">What sets topicals apart</h2>
          <p className="text-[#7a6b62] mb-8">
            Different format, different chemistry — same research-only product class.
          </p>
          <div className="grid md:grid-cols-2 gap-6" data-testid="topicals-highlights">
            {HIGHLIGHTS.map((h, i) => (
              <article
                key={h.title}
                className="bg-white border border-[#f0d8d4] rounded-2xl p-7"
                data-testid={`topicals-highlight-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                    <h.icon className="w-5 h-5 text-[#b89a6d]" />
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-[#3a2a26]">{h.title}</h3>
                </div>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{h.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Common topical actives */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-2">
            Common topical research actives
          </h2>
          <p className="text-[#7a6b62] mb-8">
            A non-exhaustive list of peptides you'll most often see in topical-research conversations.
            Availability varies — see the live catalog for current inventory.
          </p>
          <div className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl divide-y divide-[#f0d8d4]" data-testid="topicals-actives">
            {COMMON_TOPICAL_ACTIVES.map((a, i) => (
              <div key={a.name} className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`topical-active-${i}`}>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-[#3a2a26]">{a.name}</h3>
                  <p className="text-sm text-[#7a6b62]">{a.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#fdf2f4] via-[#faeae5] to-[#f7e0d2] border border-[#e8c5b8] p-10 md:p-14 text-center">
            <Sparkles className="w-7 h-7 text-[#b89a6d] mx-auto mb-4" />
            <h2 className="text-2xl md:text-4xl font-bold text-[#3a2a26] mb-4">
              Browse the topical peptide catalog
            </h2>
            <p className="text-base md:text-lg text-[#5a4a40] max-w-2xl mx-auto mb-8 leading-relaxed">
              View live availability, sizes, and current pricing on the Paramount Peptides affiliate
              storefront. Multi-unit pricing available — see our{" "}
              <Link to="/provider-access" className="text-[#b89a6d] underline hover:text-[#a08560]">
                Provider Access program
              </Link>{" "}
              for institutional-tier pricing.
            </p>
            <a
              href={SHOP_URL}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-testid="topicals-final-cta"
              onClick={TRACK("final-cta")}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
            >
              Shop Topical Peptides
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="mt-4 text-xs text-[#a09186] font-mono">
              paramountpeptides.com/product-category/topical-peptides
            </p>
          </div>
        </div>
      </section>

      {/* Disclosure */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <p className="text-sm text-[#5a4a40] leading-relaxed mb-3">
              <strong>Affiliate disclosure (FTC).</strong> PeptidesForMedSpas may receive a referral
              credit when you purchase through links on this page. Pricing, availability, and order
              terms are determined by Paramount Peptides. Your price is not affected.
            </p>
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              <strong>Research-only.</strong> All materials are research chemicals supplied for laboratory
              research purposes only — not for human or animal use, not FDA-approved drugs, not intended
              to diagnose, treat, cure, or prevent any disease. See our{" "}
              <Link to="/compliance" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="topicals-to-compliance">
                Compliance page
              </Link>{" "}
              for our full posture.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TopicalsPage;
